import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import ModalBase from '@/components/layouts/Modal/Base';
import PageContent, { StaticPage } from '@/components/layouts/Modal/Page';
import TopBannerList from '@/components/layouts/Top/BannerList';
import LargeDividendAlert from '@/components/layouts/Top/LargeDividendAlert';
import MainVisual from '@/components/layouts/Top/MainVisual';
import MultiraceList from '@/components/layouts/Top/MultiraceList';
import TopNews from '@/components/layouts/Top/News';
import RaceTypeSelector from '@/components/layouts/Top/RaceTypeSelector';
import SingleraceList from '@/components/layouts/Top/SingleraceList';
import { RACE_TYPE_PARAM_KEY } from '@/config/constants/query';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import useScript, { Status } from '@/hooks/useScript';
import {
  EntryRacerMailParam,
  GoodFormRacer,
  MultiraceProgram,
  OrderDateLargeDividend,
  RaceProgram,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { BannerAreaLabel } from '@/lib/dmm/trackingTag/type';
import { Campaign } from '@/lib/microcms/model';
import { CustomLocalStorage } from '@/lib/storage/customLocalStorage';
import {
  getLargeDividendAlertCookie,
  getVerificationStatusCookie,
  setVerificationStatusCookie,
} from '@/lib/verification';
import { RaceType as RaceTypeParam } from '@/modelTypes';
import { Content, NewsType } from '@/pages/News/types';
import { Banners } from '@/pages/Top';
import { RaceType, RecentRacePrograms } from '@/pages/Top/types';

import {
  AlertWrapper,
  AnnounceContainer,
  BannerWrapper,
  BottomBannerList,
  Container,
  ContentsWrapper,
  InformationBox,
  MiddleBannerList,
  NewsWrapper,
  PrimaryBox,
  RaceTypeSelectorWrapper,
  SideBox,
  SlideBannerList,
  TopBannerWrapper,
} from './style';

const CancelInfo = lazy(() => import('@/components/layouts/Top/CancelInfo'));
const RegisterStatus = React.lazy(
  () => import('@/components/layouts/Top/RegisterStatus'),
);
const PendingBalanceAlert = React.lazy(
  () => import('@/components/layouts/Top/PendingBalanceAlert'),
);

export const HASH_RACE = 'race';

interface TopTemplateProps {
  recentRacePrograms?: RecentRacePrograms;
  multiracePrograms: MultiraceProgram[];
  banners: Banners;
  news: Content[];
  blogs: Content[];
  campaigns: Campaign[];
  largeDividend?: OrderDateLargeDividend;
  goodWinStreakRacers: GoodFormRacer[];
  goodWinAverageRacers: GoodFormRacer[];
  entryRacers: EntryRacerMailParam[];
}

const TopTemplate: React.FC<TopTemplateProps> = ({
  recentRacePrograms,
  multiracePrograms,
  banners,
  news,
  blogs,
  campaigns,
  largeDividend,
  goodWinStreakRacers,
  goodWinAverageRacers,
  entryRacers,
}) => {
  const { user, isMember, isVerified, hasPendingBalance, isNeedSMSVerifyTop } =
    useUserContext();
  const { hash, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const paramType = queryParams.get(RACE_TYPE_PARAM_KEY);
  const [raceType, setRaceType] = useState<RaceType>(
    paramType === RaceTypeParam.Multirace
      ? RaceType.Multirace
      : RaceType.Singlerace,
  );
  const ref = useRef<HTMLDivElement>(null);

  const isChangeVerified = () => {
    if (isVerified && getVerificationStatusCookie() !== 'approved') {
      // 現在認証済みでCookieの内容が「approved」以外だったらモーダルを表示
      return true;
    }

    // Cookie期間の更新
    setVerificationStatusCookie('approved');
    return false;
  };
  const doneSlideModal = CustomLocalStorage<boolean>('done-slide-modal');
  const [openSlideModal, setOpenSlideModal] = useModal(isChangeVerified());
  const closeSlideModal = () => {
    doneSlideModal.set(true);
    setVerificationStatusCookie('approved');
    setOpenSlideModal(false);
  };

  const announcementScript = useScript(
    import.meta.env.VITE_ANNOUNCEMENT_SCRIPT_PATH,
  );

  const viewAreaLargeDividend = () => {
    if (largeDividend) {
      const isConfirmed = getLargeDividendAlertCookie(
        largeDividend.refundAmount,
      );
      if (!isConfirmed) {
        return largeDividend.updated.add(10, 'day') > dayjst();
      }
    }
    return false;
  };

  useEffect(() => {
    if (hash === `#${HASH_RACE}` && ref.current) {
      setTimeout(() => {
        window.scrollTo(0, ref.current!.offsetTop);
      }, 0);
    }
  }, [hash, ref]);

  useEffect(() => {
    if (announcementScript === Status.Ready) {
      window.DMMAnnouncement.initialize({
        barPlaceId: 73,
      });
    }
  }, [announcementScript]);

  const isSomeRaceCanceled: boolean = useMemo(() => {
    if (!recentRacePrograms) {
      return false;
    }
    return recentRacePrograms.today.some(rp => {
      if (rp.isCanceled) {
        return true;
      }
      return rp.races.some(r => r.isCanceled);
    });
  }, [recentRacePrograms?.today]);

  return (
    <Container>
      <Suspense fallback={<></>}>
        <AnnounceContainer id="announcement-bars" />
      </Suspense>
      <MainVisual mainVisuals={banners.main} />
      {(!isMember || isNeedSMSVerifyTop) && (
        <Suspense fallback={<></>}>
          <RegisterStatus />
        </Suspense>
      )}
      {hasPendingBalance && (
        <Suspense fallback={<></>}>
          <AlertWrapper>
            <PendingBalanceAlert pendingBalance={user.wallet!.pendingBalance} />
          </AlertWrapper>
        </Suspense>
      )}
      {viewAreaLargeDividend() && (
        <Suspense fallback={<></>}>
          <AlertWrapper>
            <LargeDividendAlert largeDividend={largeDividend!} />
          </AlertWrapper>
        </Suspense>
      )}
      <ContentsWrapper ref={ref}>
        <PrimaryBox>
          {isSomeRaceCanceled && (
            <Suspense fallback={<></>}>
              <CancelInfo
                racePrograms={recentRacePrograms?.today as RaceProgram[]}
              />
            </Suspense>
          )}
          <RaceTypeSelectorWrapper>
            <RaceTypeSelector raceType={raceType} setRaceType={setRaceType} />
          </RaceTypeSelectorWrapper>
          <CSSTransition
            in={raceType === RaceType.Singlerace}
            timeout={200}
            unmountOnExit
          >
            <SingleraceList
              recentRacePrograms={recentRacePrograms}
              goodWinStreakRacers={goodWinStreakRacers}
              goodWinAverageRacers={goodWinAverageRacers}
              campaigns={campaigns}
              entryRacers={entryRacers}
            />
          </CSSTransition>
          <CSSTransition
            in={raceType === RaceType.Multirace}
            timeout={200}
            unmountOnExit
          >
            <MultiraceList multiracePrograms={multiracePrograms} />
          </CSSTransition>
        </PrimaryBox>
        {banners.top.length > 0 && (
          <SideBox>
            <TopBannerWrapper>
              <SlideBannerList>
                <TopBannerList
                  bannerList={banners.top}
                  bannerArea={BannerAreaLabel.TopBanner}
                />
              </SlideBannerList>
            </TopBannerWrapper>
          </SideBox>
        )}
      </ContentsWrapper>
      <InformationBox>
        <NewsWrapper>
          {news.length > 0 && (
            <TopNews type={NewsType.News} contentsList={news} moreFlag />
          )}
          {blogs.length > 0 && (
            <TopNews type={NewsType.Blog} contentsList={blogs} moreFlag />
          )}
        </NewsWrapper>
      </InformationBox>
      {banners.middle.length > 0 && (
        <BannerWrapper>
          <MiddleBannerList>
            <TopBannerList
              bannerList={banners.middle}
              bannerArea={BannerAreaLabel.MiddleBanner}
            />
          </MiddleBannerList>
        </BannerWrapper>
      )}
      {banners.bottom.length > 0 && (
        <BottomBannerList>
          <TopBannerList
            bannerList={banners.bottom}
            bannerArea={BannerAreaLabel.FooterBanner}
          />
        </BottomBannerList>
      )}
      <ModalBase
        isOpen={openSlideModal}
        handleClose={closeSlideModal}
        desktopWidth="840px"
        mobileHeight="648px"
      >
        <PageContent
          staticPageList={[
            StaticPage.welcomeDmm,
            StaticPage.chargeCredit,
            StaticPage.chargeOther,
            StaticPage.campaign,
          ]}
          closeModal={closeSlideModal}
        />
      </ModalBase>
    </Container>
  );
};

export default TopTemplate;
